/**
 * PdfUploadDropzone — composant d'upload PDF (étape 2 du flow 3-temps).
 *
 * Ce composant est volontairement "dumb" : il ne sait rien de l'API. Il
 * orchestre le flow `initiate → PUT signed URL → complete` via les hooks
 * passés depuis le parent, et expose au parent les transitions d'état pour
 * que celui-ci puisse afficher un CTA, désactiver un formulaire pendant
 * l'upload, etc.
 *
 * Le parent fournit les `parentIds` (project_id et/ou submission_id et/ou
 * company_id) — au moins un est requis (cf. InitiateFileUploadSchema +
 * CHECK DB côté `app.files`).
 *
 * États exposés via `onStateChange` :
 *   idle         — aucun fichier sélectionné
 *   validating   — fichier déposé, on vérifie MIME/taille
 *   hashing      — calcul SHA-256 client (Web Crypto)
 *   initiating   — POST /v1/files/uploads/initiate
 *   uploading    — PUT signed URL (bucket Supabase)
 *   completing   — POST /v1/files/uploads/:id/complete
 *   done         — file en status="ready", fileId remonté au parent
 *   error        — erreur récupérable (message i18n)
 *
 * Validations client (avant tout appel réseau) :
 *   - MIME `application/pdf` (ou .pdf si le browser ne sniffe pas)
 *   - Taille ≤ 50 MB (cf. décision PO + contrainte bucket)
 *
 * Note 9.A — page_count : on NE le calcule PAS côté frontend (pas de
 * dépendance pdf-lib en 9.A). Le champ est optionnel dans
 * `InitiateFileUploadSchema` ; l'API ou le pipeline d'extraction le
 * renseignera plus tard. Si on a besoin du compteur ici plus tard, on
 * réutilisera la voie déjà en place dans `_parts/FileUploadDialog`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import {
  useCompleteFileUpload,
  useInitiateFileUpload,
  type FileRow,
} from "../../lib/queries/files";
import { beginTenantRequest } from "../../lib/tenantScope";
import { sha256OfFile, uploadToBucket, UploadToBucketError } from "../../lib/uploadPdf";

const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
const ACCEPTED_MIME = "application/pdf";

export type DropzoneState =
  | { kind: "idle" }
  | { kind: "validating" }
  | { kind: "hashing" }
  | { kind: "initiating" }
  | { kind: "uploading" }
  | { kind: "completing" }
  | { kind: "done"; file: FileRow }
  | { kind: "error"; messageKey: string; detail?: string };

type ParentIds = {
  project_id?: string;
  submission_id?: string;
  company_id?: string;
};

export type PdfUploadDropzoneProps = {
  /** Au moins un parent doit être fourni (project_id, submission_id ou company_id). */
  parentIds: ParentIds;
  /**
   * Notifié à chaque transition d'état. Le parent peut s'en servir pour
   * afficher un CTA "Continuer" quand `state.kind === "done"`.
   */
  onStateChange?: (state: DropzoneState) => void;
  /**
   * Notifié une seule fois quand l'upload réussit. Idiomatique pour le
   * parent : capturer le file_id et passer à l'étape suivante.
   */
  onUploaded?: (file: FileRow) => void;
  /** Désactive complètement le composant (ex: parent en cours de soumission). */
  disabled?: boolean;
};

export function PdfUploadDropzone({
  parentIds,
  onStateChange,
  onUploaded,
  disabled = false,
}: PdfUploadDropzoneProps) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<DropzoneState>({ kind: "idle" });
  const [fileMeta, setFileMeta] = useState<{
    name: string;
    sizeBytes: number;
  } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const initiate = useInitiateFileUpload();
  const complete = useCompleteFileUpload();

  // Garde-fou — au moins un parent (sinon erreur 422 côté API).
  const hasParent = !!parentIds.project_id || !!parentIds.submission_id || !!parentIds.company_id;

  const updateState = useCallback(
    (next: DropzoneState) => {
      setState(next);
      onStateChange?.(next);
      if (next.kind === "done") onUploaded?.(next.file);
    },
    [onStateChange, onUploaded],
  );

  /**
   * Pipeline complet — extrait pour pouvoir être lancé depuis drop ou
   * input change. Ne `throw` jamais : convertit toute erreur en état
   * `error` avec une clé i18n.
   */
  const handleFile = useCallback(
    async (file: File) => {
      if (!hasParent) {
        updateState({
          kind: "error",
          messageKey: "files.upload.errors.missing_parent",
        });
        return;
      }

      // 1) Validation MIME + extension
      updateState({ kind: "validating" });
      const isPdfMime = file.type === ACCEPTED_MIME;
      const isPdfExt = file.name.toLowerCase().endsWith(".pdf");
      if (!isPdfMime && !isPdfExt) {
        updateState({ kind: "error", messageKey: "files.upload.errors.not_pdf" });
        return;
      }

      // 2) Validation taille
      if (file.size > MAX_SIZE_BYTES) {
        updateState({ kind: "error", messageKey: "files.upload.errors.too_large" });
        return;
      }

      setFileMeta({
        name: file.name,
        sizeBytes: file.size,
      });

      const operation = beginTenantRequest(true);
      try {
        // 3) SHA-256 client (optionnel mais recommandé pour intégrité)
        updateState({ kind: "hashing" });
        let sha256: string;
        try {
          sha256 = await sha256OfFile(file);
        } catch {
          updateState({ kind: "error", messageKey: "files.upload.errors.hash_failed" });
          return;
        }

        // 4) Initiate — réserve une row `app.files` en pending + signed URL.
        //    `kind: "submission"` car ce composant est utilisé pour uploader
        //    le devis assureur d'une submission (cf. FILE_KINDS dans shared).
        //    `page_count` non envoyé : sera renseigné plus tard côté pipeline.
        updateState({ kind: "initiating" });
        let initiateResult;
        try {
          operation.assertCurrent();
          initiateResult = await initiate.mutateAsync({
            ...parentIds,
            file_name: file.name,
            mime_type: ACCEPTED_MIME,
            size_bytes: file.size,
            kind: "submission",
            sha256,
          });
        } catch (err) {
          updateState({
            kind: "error",
            messageKey: "files.upload.errors.initiate_failed",
            detail: err instanceof ApiException ? getApiErrorMessage(err, lang) : undefined,
          });
          return;
        }

        // 5) PUT direct vers la signed URL (sans header Authorization)
        updateState({ kind: "uploading" });
        try {
          operation.assertCurrent();
          await uploadToBucket({
            signal: operation.signal,
            url: initiateResult.upload.url,
            headers: initiateResult.upload.headers,
            body: file,
          });
        } catch (err) {
          updateState({
            kind: "error",
            messageKey: "files.upload.errors.bucket_failed",
            detail:
              err instanceof UploadToBucketError ? `${err.status} ${err.statusText}` : undefined,
          });
          return;
        }

        // 6) Complete — passe la row de pending à ready
        updateState({ kind: "completing" });
        try {
          operation.assertCurrent();
          const finalFile = await complete.mutateAsync({
            fileId: initiateResult.file.id,
            input: { sha256 },
          });
          operation.assertCurrent();
          updateState({ kind: "done", file: finalFile });
        } catch (err) {
          updateState({
            kind: "error",
            messageKey: "files.upload.errors.complete_failed",
            detail: err instanceof ApiException ? getApiErrorMessage(err, lang) : undefined,
          });
        }
      } finally {
        operation.finish();
      }
    },
    [complete, hasParent, initiate, parentIds, updateState],
  );

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
    // Reset pour autoriser le re-pick du même fichier après une erreur.
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const reset = () => {
    setFileMeta(null);
    updateState({ kind: "idle" });
  };

  // -------------------------------------------------------------------------
  // Sync sur unmount — évite un setState après démontage si le parent change
  // de page pendant l'upload. (TanStack mutations sont déjà robustes mais on
  // protège updateState explicitement.)
  // -------------------------------------------------------------------------
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // -------------------------------------------------------------------------
  // Rendu
  // -------------------------------------------------------------------------

  const isBusy = state.kind !== "idle" && state.kind !== "done" && state.kind !== "error";
  const isDone = state.kind === "done";
  const isError = state.kind === "error";

  const progressLabel = (() => {
    switch (state.kind) {
      case "validating":
        return t("files.upload.progress.validating");
      case "hashing":
        return t("files.upload.progress.hashing");
      case "initiating":
        return t("files.upload.progress.initiating");
      case "uploading":
        return t("files.upload.progress.uploading");
      case "completing":
        return t("files.upload.progress.completing");
      default:
        return null;
    }
  })();

  const sizeKb = fileMeta ? Math.round(fileMeta.sizeBytes / 1024) : 0;
  const sizeDisplay = sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB`;

  return (
    <div className="space-y-3">
      <div
        role="region"
        aria-label={t("files.upload.dropzone_title")}
        aria-busy={isBusy}
        className={[
          "relative rounded-xl border-2 border-dashed p-8 transition-colors",
          dragOver ? "border-brand-500 bg-brand-50" : "border-slate-300 bg-slate-50",
          disabled || isBusy ? "opacity-60" : "",
          isDone ? "border-emerald-500 bg-emerald-50" : "",
          isError ? "border-red-400 bg-red-50" : "",
        ].join(" ")}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <div className="text-2xl" aria-hidden="true">
            {isDone ? "✓" : isError ? "⚠" : "↑"}
          </div>
          {!fileMeta && !isError && (
            <>
              <p className="text-sm font-medium text-slate-800">
                {t("files.upload.dropzone_title")}
              </p>
              <p className="text-xs text-slate-500">{t("files.upload.dropzone_subtitle")}</p>
              <button
                type="button"
                className="btn-ghost mt-2"
                disabled={disabled || isBusy}
                onClick={() => inputRef.current?.click()}
              >
                {t("files.upload.picker_button")}
              </button>
              <p className="mt-1 text-[11px] text-slate-400">
                {t("files.upload.pdf_only")} · {t("files.upload.max_size_50mb")}
              </p>
            </>
          )}
          {fileMeta && !isError && (
            <div className="w-full space-y-1 text-left">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-medium text-slate-800">{fileMeta.name}</span>
                <span className="text-xs text-slate-500">{sizeDisplay}</span>
              </div>
              {progressLabel && (
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className="bg-brand-500 inline-block h-2 w-2 animate-pulse rounded-full"
                    aria-hidden="true"
                  />
                  <span className="text-xs text-slate-600">{progressLabel}</span>
                </div>
              )}
              {isDone && (
                <p className="mt-2 text-xs font-medium text-emerald-700">
                  {t("files.upload.progress.done")}
                </p>
              )}
            </div>
          )}
          {isError && (
            <div className="w-full space-y-2 text-left">
              <p className="text-sm font-medium text-red-700">{t(state.messageKey)}</p>
              {state.detail && <p className="text-xs text-red-600">{state.detail}</p>}
              <button type="button" className="btn-ghost" onClick={reset}>
                {t("common.retry")}
              </button>
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="sr-only"
          onChange={onInputChange}
          disabled={disabled || isBusy}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}
